import { vidmaxSentryPrivacyOptions } from "./utils/sentryPrivacy"
import { flg } from "@aix/webutils/src/ui/logging"
import * as Sentry from "@sentry/react"

import { AIX_ENV } from "@aix/env"

// Critical CSS - load immediately (required for first paint)
import "./index.css" // Tailwind entry; pulls the shared theme from @aix/ui/src/globals.css
import "@aix/ui/src/themes/vidmax.theme.css"

import { some } from "lodash"
import React from "react"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import { QueryClient } from "@tanstack/react-query"
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client"
import ReactDOM from "react-dom/client"

import { NavigationProvider } from "@aix/ui"

import { tanstackNavigationAdapter } from "./adapters/tanstackNavigation"
import App from "./App"
import { CACHE_VERSION, MAX_CACHE_AGE, QUERY_CACHE_EXPIRY, SKIP_PERSIST_KEYS } from "./lib/cacheConfig"
import { isProdVercelEnv } from "./lib/env"
import { ThemeProvider } from "./lib/theme-context"
import { createTRPCReactClient, getClientBuild, trpc } from "./lib/trpc"
import { TURNSTILE_EXPECTED_CLIENT_ERROR_PATTERN } from "./lib/turnstileErrors"
import { initFrontendMetrics } from "./utils/frontendMetrics"
import { shouldReloadForPreloadError } from "./utils/preloadReload"
// Non-critical CSS - loaded after initial render via lazyStyles utility
// These are deferred to improve FCP/LCP metrics:
// - "./utils/aix-react-flow-style.css" (ReactFlow diagrams)
// - "./utils/crisp-chatbox.css" (Chat widget)
// - "@aix/ui/src/themes/react-big-calendar.css" (Calendar page)
// - "react-big-calendar/lib/addons/dragAndDrop/styles.css" (Calendar DnD)
// - "react-photo-view/dist/react-photo-view.css" (Image galleries)
import { preloadNonCriticalStyles } from "./utils/lazyStyles"

const clientBuild = getClientBuild()

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: AIX_ENV,
  release: clientBuild,
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  enabled: import.meta.env.PROD,
  // Drop known-noise events at the client. These are recoverable, anonymous, or
  // upstream-vendor failures with zero user-actionable signal; they were drowning
  // out real frontend bugs in Sentry. Each pattern documented at the index it
  // ranks before the filter:
  // - "Unable to preload CSS" — stale chunk after deploy. `vite:preloadError`
  //   handler below already reloads, but Safari/Firefox sometimes raise the CSS
  //   preload error on `link` element directly without triggering Vite's event
  //   bus, so we also drop it here.
  // - "Cloudflare Turnstile ... 30xxxx/60xxxx" — visitor failed bot challenge
  //   or submitted an expired/invalid challenge response. Expected user-side,
  //   not a bug.
  // - "Invalid data" from /static/javascripts/client_default_*.js — third-party
  //   browser script noise with no app stack or affected user.
  // - "Failed to execute 'removeChild'/'insertBefore' on 'Node'" — React
  //   reconciliation race when a portal/animation unmounts mid-paint. Recovers
  //   via ErrorBoundary; no user-visible impact.
  ignoreErrors: [
    /Unable to preload CSS/i,
    /Cloudflare Turnstile.*Error: (?:30|60)\d{4}/,
    TURNSTILE_EXPECTED_CLIENT_ERROR_PATTERN,
    /Failed to execute '(?:removeChild|insertBefore)' on 'Node'/i,
  ],
  ...vidmaxSentryPrivacyOptions,
})

// Recover from stale chunk hashes after a deploy. Old tabs hold references to
// JS/CSS bundles that no longer exist on the CDN; Vite emits `vite:preloadError`
// when a dynamic import fails. `event.preventDefault()` stops Vite from
// rethrowing (otherwise Sentry captures it as a real error — was logging ~169
// events / 14d as VIDMAX-WEB-GB / EN / GG). Then force a reload so the new
// bundle hashes are picked up.
// The reload is rate-limited: a preload that fails for a reason a reload can't fix (an
// unresolvable bare specifier from an externalized dependency, an offline CDN) otherwise reloads
// forever — /create/edit did exactly that, ~3 reloads/sec, leaving the tab unusable.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault()
    if (!shouldReloadForPreloadError(window.sessionStorage, Date.now())) return
    window.location.reload()
  })
  initFrontendMetrics()
}

// import "reactflow/dist/style.css"

// import "@uploadthing/react/styles.css"
// import "@uppy/status-bar/dist/style.min.css"
// import "./utils/aix.uppy.style.css"
// import "@uppy/core/dist/style.min.css"
// import "@uppy/drag-drop/dist/style.min.css"
/**
 * Create React Query client with defaults matching Next.js setup
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false, // Disable retries for all queries
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 15 * 1000 * 60, // Every 15 minutes inactive queries are garbage collected
    },
  },
})

/**
 * Create localStorage persister for React Query
 * This caches query results to localStorage to survive page reloads
 */
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: `vidmax-query-cache-${CACHE_VERSION}`,
})

/**
 * Create tRPC client - URL is determined automatically
 */
const trpcClient = createTRPCReactClient()

/**
 * Main App Component with Providers
 */
const AppWithProviders: React.FC = () => {
  return (
    <NavigationProvider value={tanstackNavigationAdapter}>
      <ThemeProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: MAX_CACHE_AGE,
            dehydrateOptions: {
              // BLACKLIST APPROACH: Persist everything EXCEPT queries in SKIP_PERSIST_KEYS
              shouldDehydrateQuery: (query) => {
                // tRPC query keys are arrays like: [["router", "procedure"], {input}]
                // Extract the query key string from the first element
                const firstKey = query.queryKey[0]
                const queryKeyString = Array.isArray(firstKey) ? firstKey.join(".") : String(firstKey)

                // Skip if it matches any blacklist patterns
                const shouldSkip = SKIP_PERSIST_KEYS.some((skipKey) => queryKeyString.includes(skipKey))
                if (shouldSkip) {
                  flg.debug("[Cache] ❌ Skipped (blacklisted):")
                  return false
                }

                // Check if query has custom expiry time
                const customExpiry = QUERY_CACHE_EXPIRY[queryKeyString]
                if (customExpiry) {
                  const queryAge = Date.now() - (query.state.dataUpdatedAt || 0)
                  const isExpired = queryAge > customExpiry

                  if (isExpired) {
                    flg.debug("[Cache] ⏰ Expired ( ms > ms):")
                    return false
                  }
                }

                // Persist everything else!
                flg.debug("[Cache] ✅ Persisting:")
                return true
              },
            },
          }}
        >
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <App />
          </trpc.Provider>
        </PersistQueryClientProvider>
      </ThemeProvider>
    </NavigationProvider>
  )
}

/**
 * Crisp chat widget (production only). Identification happens in AppLayout
 * via identifyUserCrispChat once the user is authenticated.
 */
if (isProdVercelEnv) {
  // @ts-expect-error -- Crisp globals not typed on window
  window.$crisp = []
  // @ts-expect-error -- Crisp globals not typed on window
  window.CRISP_WEBSITE_ID = "4506760f-d9e2-4fc1-9e2c-b70b73d742a7"
  const crispScript = document.createElement("script")
  crispScript.src = "https://client.crisp.chat/l.js"
  crispScript.async = true
  document.head.appendChild(crispScript)
}

/**
 * Dev environment setup (non-production environments)
 */
if (!isProdVercelEnv) {
  // Add visual border indicator
  document.body.classList.add("dev-environment")

  // Show the active dev-server port so concurrent checkouts are easy to identify.
  const devPort = import.meta.env.VITE_PORT || window.location.port
  const devPrefix = `[:${devPort}]`

  // Add prefix to browser tab title
  const originalTitle = document.title
  document.title = `${devPrefix} ${originalTitle}`

  // Keep title updated when it changes (e.g., route changes)
  const titleObserver = new MutationObserver(() => {
    if (!document.title.startsWith(devPrefix)) {
      document.title = `${devPrefix} ${document.title}`
    }
  })
  titleObserver.observe(document.querySelector("title") || document.head, {
    subtree: true,
    characterData: true,
    childList: true,
  })
}

/**
 * Render the app
 */
const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error("Root element not found")
}

const root = ReactDOM.createRoot(rootElement)
root.render(
  <React.StrictMode>
    <AppWithProviders />
  </React.StrictMode>,
)

// Preload non-critical styles after initial render
// This defers loading of feature-specific CSS to improve FCP/LCP
preloadNonCriticalStyles()
